import {
  OMIT,
  PLAIN_ENTRIES,
  type PlainEntry,
} from "@/lib/tool-kit/style-checkers/plain-language-words";
import { complexitySyllables } from "@/lib/tool-kit/style-checkers/syllables";
import {
  insideAny,
  isSentenceStart,
  phraseRegex,
  quotedSpans,
  type Span,
  withoutOverlaps,
  wordTokens,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Complex words, two ways.
 *
 * 1. The plain-language list. Words and phrases from the Federal Plain Language
 *    Guidelines ("Use simple words and phrases") with the alternatives it suggests:
 *    utilize → use, commence → begin, in order to → to. Verbs are matched in every form
 *    (utilize, utilizes, utilized, utilizing) and the suggested replacement is put into
 *    the same form where it can be.
 * 2. Long words. Any other word with `minSyllables` or more syllables, counted the way
 *    the Gunning fog index counts a "complex word": proper nouns, hyphenated compounds
 *    and ALL-CAPS words are not complex, and an -ing, -ed or -es ending does not add
 *    to the count.
 *
 * Neither list says a word is wrong. A long word is often the exact word, and the plain
 * list was written for government documents, not research papers: the reader decides.
 */

export type VerbForm = "base" | "third" | "past" | "gerund";

export interface PlainMatch extends Span {
  text: string;
  entry: PlainEntry;
  form: VerbForm;
  /** Lowercase, single-spaced text of the match: the identity used for grouping. */
  key: string;
  /** Alternatives with the entry's verb form applied; OMIT stays OMIT. */
  alternatives: string[];
}

export interface LongWord extends Span {
  text: string;
  key: string;
  syllables: number;
}

export interface ComplexOptions {
  /** 3 is the fog-index definition; 4 finds only the longest words. */
  minSyllables: number;
  usePlainList: boolean;
  /** Skip quotations: the wording belongs to someone else. */
  skipQuotes: boolean;
  /** Lowercase words the writer accepts. */
  keep: ReadonlySet<string>;
}

export const DEFAULT_COMPLEX_OPTIONS: ComplexOptions = {
  minSyllables: 3,
  usePlainList: true,
  skipQuotes: true,
  keep: new Set(),
};

/* ------------------------------------------------------------ verb forms */

const RE_ENDS_E = /e$/;
const RE_ENDS_EE = /(?:ee|ye|oe)$/;
const RE_CONSONANT_Y = /[^aeiou]y$/;
const RE_SIBILANT = /(?:s|x|z|ch|sh)$/;

interface Irregular {
  third: string;
  past: string;
  gerund: string;
}

const IRREGULAR: Readonly<Record<string, Irregular>> = {
  do: { third: "does", past: "did", gerund: "doing" },
  go: { third: "goes", past: "went", gerund: "going" },
  give: { third: "gives", past: "gave", gerund: "giving" },
  send: { third: "sends", past: "sent", gerund: "sending" },
  keep: { third: "keeps", past: "kept", gerund: "keeping" },
  buy: { third: "buys", past: "bought", gerund: "buying" },
  tell: { third: "tells", past: "told", gerund: "telling" },
  find: { third: "finds", past: "found", gerund: "finding" },
  make: { third: "makes", past: "made", gerund: "making" },
  see: { third: "sees", past: "saw", gerund: "seeing" },
  show: { third: "shows", past: "showed", gerund: "showing" },
  begin: { third: "begins", past: "began", gerund: "beginning" },
  think: { third: "thinks", past: "thought", gerund: "thinking" },
  pay: { third: "pays", past: "paid", gerund: "paying" },
  meet: { third: "meets", past: "met", gerund: "meeting" },
  leave: { third: "leaves", past: "left", gerund: "leaving" },
  take: { third: "takes", past: "took", gerund: "taking" },
  set: { third: "sets", past: "set", gerund: "setting" },
  let: { third: "lets", past: "let", gerund: "letting" },
  cut: { third: "cuts", past: "cut", gerund: "cutting" },
  hurt: { third: "hurts", past: "hurt", gerund: "hurting" },
  lose: { third: "loses", past: "lost", gerund: "losing" },
  have: { third: "has", past: "had", gerund: "having" },
  get: { third: "gets", past: "got", gerund: "getting" },
  run: { third: "runs", past: "ran", gerund: "running" },
  spend: { third: "spends", past: "spent", gerund: "spending" },
  choose: { third: "chooses", past: "chose", gerund: "choosing" },
  prove: { third: "proves", past: "proved", gerund: "proving" },
  build: { third: "builds", past: "built", gerund: "building" },
  hold: { third: "holds", past: "held", gerund: "holding" },
  drop: { third: "drops", past: "dropped", gerund: "dropping" },
  stop: { third: "stops", past: "stopped", gerund: "stopping" },
  permit: { third: "permits", past: "permitted", gerund: "permitting" },
  submit: { third: "submits", past: "submitted", gerund: "submitting" },
  transmit: { third: "transmits", past: "transmitted", gerund: "transmitting" },
};

function regularForm(base: string, form: VerbForm): string {
  switch (form) {
    case "third":
      if (RE_CONSONANT_Y.test(base)) {
        return `${base.slice(0, -1)}ies`;
      }
      return RE_SIBILANT.test(base) ? `${base}es` : `${base}s`;
    case "past":
      if (RE_ENDS_E.test(base)) {
        return `${base}d`;
      }
      return RE_CONSONANT_Y.test(base)
        ? `${base.slice(0, -1)}ied`
        : `${base}ed`;
    case "gerund":
      return RE_ENDS_E.test(base) && !RE_ENDS_EE.test(base)
        ? `${base.slice(0, -1)}ing`
        : `${base}ing`;
    default:
      return base;
  }
}

/** The verb `base` in `form`. Irregular verbs the alternatives use are tabled. */
export function verbForm(base: string, form: VerbForm): string {
  if (form === "base") {
    return base;
  }
  const irregular = IRREGULAR[base];
  return irregular ? irregular[form] : regularForm(base, form);
}

/** "carry out" in the past → "carried out". Only the first word changes. */
function inflectPhrase(phrase: string, form: VerbForm): string {
  if (form === "base" || phrase === OMIT) {
    return phrase;
  }
  const [first, ...rest] = phrase.split(" ");
  return [verbForm(first, form), ...rest].join(" ");
}

/* ------------------------------------------------------------ the list */

interface FormEntry {
  entry: PlainEntry;
  form: VerbForm;
}

const RE_SPACES = /\s+/g;
const RE_CURLY = /[’‘]/g;

function formKey(text: string): string {
  return text.toLowerCase().replace(RE_CURLY, "'").replace(RE_SPACES, " ");
}

interface PlainIndex {
  regex: RegExp;
  byForm: ReadonlyMap<string, FormEntry>;
}

let index: PlainIndex | null = null;

function plainIndex(): PlainIndex {
  if (index) {
    return index;
  }
  const byForm = new Map<string, FormEntry>();
  const forms = ["base", "third", "past", "gerund"] as const;
  for (const entry of PLAIN_ENTRIES) {
    const wanted = entry.verb ? forms : (["base"] as const);
    for (const form of wanted) {
      const text = entry.verb ? verbForm(entry.word, form) : entry.word;
      if (!byForm.has(text)) {
        byForm.set(text, { entry, form });
      }
    }
  }
  index = { regex: phraseRegex([...byForm.keys()]), byForm };
  return index;
}

/* ------------------------------------------------------------ the scan */

export interface ComplexReport {
  words: number;
  /** Words with `minSyllables` or more (fog-index definition), plain-list matches included. */
  complex: number;
  plain: PlainMatch[];
  long: LongWord[];
}

const RE_UPPER_START = /^\p{Lu}/u;
const RE_ALL_CAPS = /^\p{Lu}[\p{Lu}'’]*$/u;
const RE_DIGIT = /\p{N}/u;
const RE_HYPHEN = /[‐‑-]/;

function plainMatches(text: string, quoted: readonly Span[]): PlainMatch[] {
  const { regex, byForm } = plainIndex();
  const found: PlainMatch[] = [];
  for (const match of text.matchAll(regex)) {
    const key = formKey(match[0]);
    const hit = byForm.get(key);
    const start = match.index;
    const end = start + match[0].length;
    if (!hit || insideAny(quoted, start, end)) {
      continue;
    }
    found.push({
      start,
      end,
      text: match[0],
      entry: hit.entry,
      form: hit.form,
      key,
      alternatives: hit.entry.alternatives.map((alt) =>
        hit.entry.verb ? inflectPhrase(alt, hit.form) : alt
      ),
    });
  }
  return withoutOverlaps(found);
}

function isEligibleLongWord(
  text: string,
  token: { text: string; start: number },
  options: ComplexOptions
): boolean {
  const word = token.text;
  if (RE_DIGIT.test(word) || RE_HYPHEN.test(word) || RE_ALL_CAPS.test(word)) {
    return false;
  }
  if (options.keep.has(word.toLowerCase().normalize("NFC"))) {
    return false;
  }
  return !(RE_UPPER_START.test(word) && !isSentenceStart(text, token.start));
}

function overlapsAny(
  spans: readonly Span[],
  start: number,
  end: number
): boolean {
  return spans.some((span) => start < span.end && end > span.start);
}

/** Find complex words in `text`. Offsets index the reader's own text. */
export function findComplexWords(
  text: string,
  options: ComplexOptions = DEFAULT_COMPLEX_OPTIONS
): ComplexReport {
  const quoted = options.skipQuotes ? quotedSpans(text) : [];
  const plain = options.usePlainList
    ? plainMatches(text, quoted).filter(
        (match) =>
          !(options.keep.has(match.key) || options.keep.has(match.entry.word))
      )
    : [];
  const tokens = wordTokens(text);
  const long: LongWord[] = [];
  let complex = 0;
  for (const token of tokens) {
    if (
      insideAny(quoted, token.start, token.end) ||
      !isEligibleLongWord(text, token, options)
    ) {
      continue;
    }
    const syllables = complexitySyllables(token.text);
    if (syllables < options.minSyllables) {
      continue;
    }
    complex += 1;
    if (!overlapsAny(plain, token.start, token.end)) {
      long.push({
        start: token.start,
        end: token.end,
        text: token.text,
        key: token.lower.normalize("NFC"),
        syllables,
      });
    }
  }
  return { words: tokens.length, complex, plain, long };
}

/* ------------------------------------------------------------ grouping */

export interface PlainGroup {
  key: string;
  entry: PlainEntry;
  form: VerbForm;
  /** First spelling seen, in its own case. */
  text: string;
  alternatives: string[];
  spans: Span[];
}

export interface LongGroup {
  key: string;
  text: string;
  syllables: number;
  spans: Span[];
}

export function groupPlain(matches: readonly PlainMatch[]): PlainGroup[] {
  const groups = new Map<string, PlainGroup>();
  for (const match of matches) {
    const group = groups.get(match.key);
    const span = { start: match.start, end: match.end };
    if (group) {
      group.spans.push(span);
    } else {
      groups.set(match.key, {
        key: match.key,
        entry: match.entry,
        form: match.form,
        text: match.text,
        alternatives: match.alternatives,
        spans: [span],
      });
    }
  }
  return [...groups.values()].sort(
    (a, b) => b.spans.length - a.spans.length || a.key.localeCompare(b.key)
  );
}

export function groupLong(words: readonly LongWord[]): LongGroup[] {
  const groups = new Map<string, LongGroup>();
  for (const word of words) {
    const group = groups.get(word.key);
    const span = { start: word.start, end: word.end };
    if (group) {
      group.spans.push(span);
    } else {
      groups.set(word.key, {
        key: word.key,
        text: word.text,
        syllables: word.syllables,
        spans: [span],
      });
    }
  }
  return [...groups.values()].sort(
    (a, b) =>
      b.syllables - a.syllables ||
      b.spans.length - a.spans.length ||
      a.key.localeCompare(b.key)
  );
}

/** The replacement in the case of the word it replaces. */
function caseLike(original: string, replacement: string): string {
  return RE_UPPER_START.test(original)
    ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
    : replacement;
}

/** Replace every span with `replacement`, each in the case it started in. */
export function replaceSpans(
  text: string,
  spans: readonly Span[],
  replacement: string
): string {
  let result = text;
  for (const span of [...spans].sort((a, b) => b.start - a.start)) {
    result =
      result.slice(0, span.start) +
      caseLike(result.slice(span.start, span.end), replacement) +
      result.slice(span.end);
  }
  return result;
}

const RE_KEEP_SPLIT = /[\n,;]+/;

export function parseKeepList(raw: string): Set<string> {
  return new Set(
    raw
      .split(RE_KEEP_SPLIT)
      .map((word) => formKey(word.normalize("NFC")).trim())
      .filter((word) => word.length > 0)
  );
}
